// market-engine.js — Single-market backtest engine for YDM curve analysis
// No UI dependencies. Importable by React, Node scripts, or agent tools.

const TARGET_U = 0.9;
const SECONDS_PER_YEAR = 365 * 24 * 3600;

// ── YDM Curve ────────────────────────────────────────────────────────────────

/**
 * V2 fixed-slope YDM curve. Linear interpolation with kink at TARGET_U = 0.9.
 * Returns the yield share allocated to JT at a given utilization.
 *
 * Below target: slope = (yT - y0) / 0.9
 * Above target: slope = (y100 - yT) / 0.1
 *
 * @param {number} u           - Utilization [0, 1]
 * @param {{ y0: number, yT: number, y100: number }} ydm
 * @returns {number} Yield share clamped to [0, 1]
 */
function computeYieldAtUtilization(u, { y0, yT, y100 }) {
  const uClamped = Math.min(Math.max(u, 0), 1);
  let y;
  if (uClamped <= TARGET_U) {
    const slope = (yT - y0) / TARGET_U;
    y = y0 + slope * uClamped;
  } else {
    const slope = (y100 - yT) / (1 - TARGET_U);
    y = yT + slope * (uClamped - TARGET_U);
  }
  return Math.min(Math.max(y, 0), 1);
}

// ── Backtest ─────────────────────────────────────────────────────────────────

/**
 * Backtest a single DeFi market by running a YDM curve against historic NAV
 * data to produce backtested ST and JT returns.
 *
 * @param {Object} opts
 * @param {{ date: string, nav: number }[]} opts.navTimeseries - Daily NAV history
 * @param {{ y0: number, yT: number, y100: number }} opts.ydm  - YDM curve params
 * @param {{ minCoverage: number, recoveryDuration: number, stFee: number, jtFee: number, ysFee: number }} opts.params
 * @param {{ finalizedLossThreshold: number, selfLiquidationBonus: number }} opts.liquidation
 * @param {number} opts.utilization - Assumed constant utilization [0, 1]
 *
 * @returns {{ dates, st, jt, drawdownEvents, summary }}
 */
function backtestMarket({ navTimeseries, ydm, params, liquidation, utilization }) {
  const {
    minCoverage = 0,
    recoveryDuration = 7,
    stFee = 0,
    jtFee = 0,
    ysFee = 0,
  } = params || {};
  const { finalizedLossThreshold = 0 } = liquidation || {};

  const n = navTimeseries.length;
  if (n < 2) {
    return emptyResult();
  }

  const yieldShare = computeYieldAtUtilization(utilization, ydm);

  // Use actual utilization for pool composition and coverage calculations.
  // TARGET_U (0.9) is only the YDM curve kink — not the pool structure.
  const u = utilization || TARGET_U;

  // Effective FLT: measured as drawdown from the min-coverage NAV level.
  // Buffer = drawdown to go from current coverage to min coverage.
  const coverageBuffer = minCoverage > 0
    ? (minCoverage / u - minCoverage) / (1 - minCoverage)
    : 0;
  const effectiveFLT = finalizedLossThreshold > 0
    ? 1 - (1 - coverageBuffer) * (1 - finalizedLossThreshold)
    : 0;

  // J:S ratio at actual utilization
  const jsRatio = minCoverage > 0
    ? minCoverage / (u - minCoverage)
    : 0;

  // Pool composition: track absolute values for proper FIXED_TERM waterfall
  const stFrac = jsRatio > 0 ? 1 / (1 + jsRatio) : 1;
  const jtFrac = jsRatio > 0 ? jsRatio / (1 + jsRatio) : 0;

  // Output arrays (day 0 = first entry, no return computed)
  const dates = [navTimeseries[0].date];
  const stCumReturn = [0];
  const jtCumReturn = [0];
  const stTrailingApy = [0];
  const jtTrailingApy = [0];
  const drawdownEvents = [];

  // Running state — absolute values (notional $1 initial pool)
  let stVal = stFrac;
  let jtVal = jtFrac;
  let stPeakVal = stFrac;
  let maxDrawdown = 0;

  // JT impermanent loss: absolute NAV amount that JT covered for ST.
  // During FIXED_TERM, appreciation first repays jtIL before normal distribution.
  // Fees stop accruing during FIXED_TERM recovery. Excess after IL recovery
  // splits normally via YDM with fees (market returns to PERPETUAL).
  let jtIL = 0;

  // Episode tracking for recovery duration
  let episodePeakNav = navTimeseries[0].nav;
  let episodeStartMs = 0; // calendar timestamp (ms) of episode start

  // FIXED_TERM period tracking — for APY chart visualization
  const fixedTermPeriods = [];
  let fixedTermStartDate = null;

  const TRAILING_WINDOW_DAYS = 7;

  // Store per-day ST/JT values for post-loop trailing APY computation.
  // Using value ratios (like the underlying APY) avoids gap-day distortions
  // that occur when compounding daily returns over a sliding window.
  const stVals = [stFrac];
  const jtVals = [jtFrac];

  for (let i = 1; i < n; i++) {
    const dailyReturn = navTimeseries[i].nav / navTimeseries[i - 1].nav - 1;
    const nav = navTimeseries[i].nav;
    dates.push(navTimeseries[i].date);

    const currentMs = new Date(navTimeseries[i].date).getTime();

    const jtILBefore = jtIL; // snapshot before finalization (for trailing APY reset)

    // Check recovery duration expiry BEFORE gain computation,
    // so finalization day distributes yield under PERPETUAL rules.
    // Only finalize if NAV hasn't recovered — if it has, let FIXED_TERM
    // waterfall handle natural recovery (matches analyzeDrawdowns logic).
    const calendarDaysSinceEpisodeStart = episodeStartMs > 0 ? (currentMs - episodeStartMs) / 86400000 : 0;
    if (jtIL > 0 && recoveryDuration > 0 && calendarDaysSinceEpisodeStart >= recoveryDuration && nav < episodePeakNav) {
      jtIL = 0; // loss finalized — JT permanently loses this
      episodePeakNav = nav; // new baseline
    }
    const poolVal = stVal + jtVal;
    const poolGain = poolVal * dailyReturn;
    let stGain = 0; // absolute gain for ST this day
    let jtGain = 0; // absolute gain for JT this day

    if (dailyReturn >= 0) {
      if (jtIL > 0 && jsRatio > 0) {
        // ── FIXED_TERM: priority waterfall ──
        // Step 1: recover JT coverage IL (no fees)
        const recovery = Math.min(poolGain, jtIL);
        const excess = poolGain - recovery;
        jtIL -= recovery;
        jtGain += recovery;

        // Step 2: excess split via YDM (PERPETUAL rules, fees resume)
        if (excess > 0) {
          const excessRate = excess / poolVal;
          // JT own yield + risk premium
          jtGain += jtVal * excessRate * (1 - jtFee)
                  + stVal * excessRate * yieldShare * (1 - ysFee);
          // ST yield
          stGain += stVal * excessRate * (1 - yieldShare) * (1 - stFee);
        }

        // Check if IL fully recovered → return to PERPETUAL
        if (jtIL <= 0) {
          jtIL = 0;
          episodePeakNav = nav;
        }
      } else if (jsRatio > 0) {
        // ── PERPETUAL: normal yield split ──
        // JT: own yield (after jtFee) + risk premium from ST yield (after ysFee)
        jtGain = jtVal * dailyReturn * (1 - jtFee)
               + stVal * dailyReturn * yieldShare * (1 - ysFee);
        // ST: keeps (1 - yieldShare) of its yield, after stFee
        stGain = stVal * dailyReturn * (1 - yieldShare) * (1 - stFee);
      } else {
        // No JT — all yield to ST
        stGain = poolGain * (1 - stFee);
      }

      // Update episode peak for PERPETUAL
      if (jtIL <= 0 && nav > episodePeakNav) {
        episodePeakNav = nav;
      }
    } else {
      // ── Negative day: JT absorbs losses, ST protected ──
      if (jsRatio > 0) {
        const absLoss = -poolGain;

        if (absLoss <= jtVal) {
          // JT absorbs entire pool loss
          jtGain = poolGain;
          stGain = 0;
          jtIL += absLoss;
        } else {
          // JT wiped out, excess passes to ST
          jtGain = -jtVal;
          stGain = poolGain - jtGain;
          jtIL += jtVal;
        }

        // Enter FIXED_TERM if not already
        if (jtIL > 0 && nav < episodePeakNav) {
          // Track episode start for recovery duration (only on first loss day)
          if (navTimeseries[i - 1].nav >= episodePeakNav) {
            episodeStartMs = currentMs;
          }
        }

        // If recoveryDuration=0, immediately finalize: loss is permanent, no recovery period.
        // JT capital is already reduced (jtVal decreased); clearing jtIL means the next
        // gain day uses PERPETUAL rules (normal YDM split) instead of FIXED_TERM waterfall.
        if (recoveryDuration === 0 && jtIL > 0) {
          jtIL = 0;
          episodePeakNav = nav;
        }

        // FLT breach: if drawdown from episode peak exceeds effective FLT, finalize loss.
        if (effectiveFLT > 0 && jtIL > 0 && episodePeakNav > 0) {
          const ddFromPeak = (episodePeakNav - nav) / episodePeakNav;
          if (ddFromPeak >= effectiveFLT) {
            jtIL = 0;
            episodePeakNav = nav;
          }
        }
      } else {
        stGain = poolGain;
        jtGain = 0;
      }

      // Track drawdown events
      const stDaily0 = stVal > 0 ? stGain / stVal : 0;
      const jtDaily0 = jtVal > 0 ? jtGain / jtVal : 0;
      drawdownEvents.push({
        date: navTimeseries[i].date,
        dayIndex: i,
        dailyReturn,
        stDailyReturn: stDaily0,
        jtDailyReturn: jtDaily0,
      });
    }

    // Update absolute values
    stVal += stGain;
    jtVal += jtGain;

    // Store values for post-loop trailing APY computation
    stVals.push(stVal);
    jtVals.push(jtVal);

    // Per-unit cumulative returns
    stCumReturn.push(stFrac > 0 ? stVal / stFrac - 1 : 0);
    jtCumReturn.push(jtFrac > 0 ? jtVal / jtFrac - 1 : 0);

    // Track max drawdown (ST)
    if (stVal > stPeakVal) stPeakVal = stVal;
    const dd = (stPeakVal - stVal) / stPeakVal;
    if (dd > maxDrawdown) maxDrawdown = dd;

    // ── FIXED_TERM period tracking ──
    const inFixedTerm = jtIL > 0;
    const wasInFixedTerm = jtILBefore > 0;

    // Track FIXED_TERM start — capture JT value before the first loss
    if (inFixedTerm && !wasInFixedTerm) {
      fixedTermStartDate = navTimeseries[i].date;
    }
    // Track FIXED_TERM end
    if (!inFixedTerm && wasInFixedTerm) {
      fixedTermPeriods.push({ start: fixedTermStartDate, end: navTimeseries[i].date });
      fixedTermStartDate = null;
    }
  }

  // Close any open FIXED_TERM period at end of series
  if (fixedTermStartDate) {
    fixedTermPeriods.push({ start: fixedTermStartDate, end: navTimeseries[n - 1].date });
  }

  // ── FT Influence Zones ──
  const lastDateStr = navTimeseries[n - 1].date;
  const fixedTermInfluenceZones = clipAndMergeZones(
    fixedTermPeriods
      .filter(p => p.end !== lastDateStr)
      .map(p => {
        const endMs = new Date(p.end).getTime();
        const influenceEnd = new Date(endMs + TRAILING_WINDOW_DAYS * 86400000).toISOString().slice(0, 10);
        return { start: p.end, end: influenceEnd < lastDateStr ? influenceEnd : lastDateStr };
      }),
    fixedTermPeriods
  );

  // ── Trailing 7-day APY (post-loop) ──
  // Computed from stored ST/JT values using the same lookback approach as
  // the underlying APY: find the entry closest to 7 calendar days back and
  // compute the annualized return ratio. No manipulation around FIXED_TERM —
  // actual values shown everywhere; visual annotations communicate context.

  for (let i = 1; i < n; i++) {
    const currentMs = new Date(navTimeseries[i].date).getTime();
    const cutoffMs = currentMs - TRAILING_WINDOW_DAYS * 86400000;
    let j = i - 1;
    while (j > 0 && new Date(navTimeseries[j].date).getTime() > cutoffMs) j--;
    const startMs = new Date(navTimeseries[j].date).getTime();
    const calendarDays = Math.max((currentMs - startMs) / 86400000, 1);

    const stReturn = stVals[j] > 0 ? stVals[i] / stVals[j] : 1;
    const jtReturn = jtVals[j] > 0 ? jtVals[i] / jtVals[j] : 1;
    stTrailingApy.push(Math.pow(stReturn, 365 / calendarDays) - 1);
    jtTrailingApy.push(Math.pow(jtReturn, 365 / calendarDays) - 1);
  }

  // ── Daily return (raw single-day percentage, not annualized) ──
  const stDailyReturn = [0];
  const jtDailyReturn = [0];
  for (let i = 1; i < n; i++) {
    stDailyReturn.push(stVals[i - 1] > 0 ? stVals[i] / stVals[i - 1] - 1 : 0);
    jtDailyReturn.push(jtVals[i - 1] > 0 ? jtVals[i] / jtVals[i - 1] - 1 : 0);
  }

  // Summary — use actual date range, not entry count
  const firstDate = new Date(navTimeseries[0].date);
  const lastDate = new Date(navTimeseries[n - 1].date);
  const totalDays = n > 1 ? Math.round((lastDate - firstDate) / 86400000) : 0;
  const stTotal = stFrac > 0 ? stVal / stFrac - 1 : 0;
  const jtTotal = jtFrac > 0 ? jtVal / jtFrac - 1 : 0;
  const stCum = stFrac > 0 ? stVal / stFrac : 1;
  const jtCum = jtFrac > 0 ? jtVal / jtFrac : 1;
  const stAnnualized = totalDays > 0 ? Math.pow(stCum, 365 / totalDays) - 1 : 0;
  const jtAnnualized = totalDays > 0 ? Math.pow(jtCum, 365 / totalDays) - 1 : 0;

  return {
    dates,
    st: { cumReturn: stCumReturn, trailingApy: stTrailingApy, dailyReturn: stDailyReturn },
    jt: { cumReturn: jtCumReturn, trailingApy: jtTrailingApy, dailyReturn: jtDailyReturn },
    fixedTermPeriods,
    fixedTermInfluenceZones,
    drawdownEvents,
    summary: {
      stAnnualized,
      jtAnnualized,
      stTotal,
      jtTotal,
      maxDrawdown,
      days: totalDays,
    },
  };
}

// ── Drawdown Analysis ────────────────────────────────────────────────────────

/**
 * Analyze drawdown episodes from NAV timeseries and determine when/if JT
 * losses are finalized, with the cause of each loss event.
 *
 * Protocol logic:
 * - A drawdown episode starts when NAV drops below its running peak.
 *   This is the PERPETUAL → FIXED_TERM transition in the protocol.
 * - The recovery timer starts at episode start (first loss day) and does
 *   NOT reset on deeper troughs — matching on-chain behavior where
 *   fixedTermEndTimestamp is set once on the state transition.
 * - The trough is the lowest NAV within the episode (tracked for reporting).
 * - If recoveryDuration > 0 and NAV hasn't recovered to peak within
 *   recoveryDuration days of episode start, JT losses are finalized.
 * - After finalization, the peak resets to the current NAV (loss absorbed).
 * - JT loss is amplified: drawdown × utilization / minCoverage.
 * - If drawdown exceeds minCoverage / utilization, JT coverage is exhausted.
 * - If drawdown exceeds finalizedLossThreshold, liquidation is triggered.
 *
 * @param {Object} opts
 * @param {{ date: string, nav: number }[]} opts.navTimeseries
 * @param {{ minCoverage: number, recoveryDuration: number }} opts.params
 * @param {{ finalizedLossThreshold: number }} opts.liquidation
 * @param {number} [opts.utilization] - Actual utilization [0, 1]; defaults to TARGET_U
 * @returns {{ episodes: Array, jtLossEvents: Array, drawdownSeries: Array<{ date: string, drawdown: number }> }}
 */
function analyzeDrawdowns({ navTimeseries, params, liquidation, utilization }) {
  const n = navTimeseries.length;
  if (n < 2) return { episodes: [], jtLossEvents: [], drawdownSeries: [] };

  const { minCoverage = 0, recoveryDuration = 7 } = params || {};
  const { finalizedLossThreshold = 0 } = liquidation || {};
  const u = utilization || TARGET_U;

  // Coverage exhaustion level: drawdown at which JT is fully depleted
  const coverageExhaustionPct = minCoverage > 0 ? minCoverage / u : Infinity;

  // FLT is enforced as drawdown from the min-coverage level, not from current
  // coverage. At actual utilization, coverage = minCoverage/u which may be
  // higher than minCoverage. The buffer must be consumed before FLT applies.
  const coverageBuffer = minCoverage > 0
    ? (minCoverage / u - minCoverage) / (1 - minCoverage)
    : 0;
  // FLT is a drawdown from the min-coverage NAV level, so compound:
  // totalDrawdown = 1 - (1 - buffer)(1 - FLT)
  const effectiveFLT = finalizedLossThreshold > 0
    ? 1 - (1 - coverageBuffer) * (1 - finalizedLossThreshold)
    : 0;

  const episodes = [];
  const jtLossEvents = [];

  // ── Chart series: drawdown from all-time peak (never resets) ──
  // Represents the system's effective LTV — losses aren't erased by finalization.
  const drawdownSeries = [];
  let runningPeak = navTimeseries[0].nav;
  for (let i = 0; i < n; i++) {
    if (navTimeseries[i].nav > runningPeak) runningPeak = navTimeseries[i].nav;
    drawdownSeries.push({
      date: navTimeseries[i].date,
      drawdown: (navTimeseries[i].nav - runningPeak) / runningPeak,
    });
  }

  // ── Episode detection: uses internal peak that resets after finalization ──
  // This detects new drawdown episodes even after a prior loss was finalized.
  let episodePeak = navTimeseries[0].nav;
  let inEpisode = false;
  let episodeStartIdx = 0;
  let troughIdx = 0;
  let troughNav = navTimeseries[0].nav;

  function finalizeEpisode(endIdx, recovered) {
    const drawdownPct = (troughNav - episodePeak) / episodePeak; // negative, trough-based
    // Drawdown at the actual finalization date — may be less severe than trough
    // if there was partial recovery before the timer fired.
    const endDrawdownPct = (navTimeseries[endIdx].nav - episodePeak) / episodePeak; // negative
    const durationDays = Math.round(
      (new Date(navTimeseries[endIdx].date) - new Date(navTimeseries[episodeStartIdx].date)) / 86400000
    );

    const episode = {
      startDate: navTimeseries[episodeStartIdx].date,
      troughDate: navTimeseries[troughIdx].date,
      endDate: navTimeseries[endIdx].date,
      drawdownPct,
      endDrawdownPct,
      durationDays,
      recovered,
      // Drawdown levels on the all-time-peak chart (for box positioning)
      startDrawdown: drawdownSeries[episodeStartIdx - 1].drawdown,
      troughDrawdown: drawdownSeries[troughIdx].drawdown,
      jtLossFinalized: false,
      jtLossPct: 0,
      jtLossCause: null,
    };

    // Determine if JT losses are finalized and the cause
    if (!recovered) {
      const absDd = -drawdownPct; // positive trough magnitude

      // Check causes in order of severity
      if (effectiveFLT > 0 && absDd >= effectiveFLT) {
        episode.jtLossFinalized = true;
        episode.jtLossCause = 'Liquidation threshold breached';
        // JT loss is -100% only if drawdown exceeded coverage capacity;
        // otherwise proportional to the drawdown vs coverage ratio.
        if (minCoverage > 0 && absDd >= coverageExhaustionPct) {
          episode.jtLossPct = -1.0;
        } else if (minCoverage > 0) {
          episode.jtLossPct = endDrawdownPct * u / minCoverage;
        } else {
          episode.jtLossPct = 0;
        }
      } else if (minCoverage > 0 && absDd >= coverageExhaustionPct) {
        episode.jtLossFinalized = true;
        episode.jtLossCause = 'JT coverage exhausted';
        episode.jtLossPct = -1.0;
      } else if (recoveryDuration >= 0 && minCoverage > 0) {
        episode.jtLossFinalized = true;
        episode.jtLossCause = recoveryDuration === 0 ? 'Immediately booked' : 'Recovery duration exceeded';
        // Use NAV at finalization date, not trough — JT crystallises the
        // unrecovered IL at the moment the timer fires, which matches backtestMarket.
        episode.jtLossPct = endDrawdownPct * u / minCoverage;
      }
    }

    episodes.push(episode);
    if (episode.jtLossFinalized) {
      jtLossEvents.push(episode);
    }
  }

  for (let i = 1; i < n; i++) {
    const nav = navTimeseries[i].nav;

    if (nav >= episodePeak) {
      // NAV at or above episode peak — episode resolved if we were in one
      if (inEpisode) {
        finalizeEpisode(i, true);
        inEpisode = false;
      }
      episodePeak = nav;
    } else {
      // NAV below episode peak
      if (!inEpisode) {
        inEpisode = true;
        episodeStartIdx = i;
        troughIdx = i;
        troughNav = nav;
      } else {
        if (nav < troughNav) {
          troughIdx = i;
          troughNav = nav;
        }

        const daysSinceStart = (new Date(navTimeseries[i].date) - new Date(navTimeseries[episodeStartIdx].date)) / 86400000;
        if (recoveryDuration === 0 || (recoveryDuration > 0 && daysSinceStart >= recoveryDuration)) {
          finalizeEpisode(i, false);
          // Reset episode peak to current NAV for detecting subsequent episodes
          episodePeak = nav;
          inEpisode = false;
        }
      }
    }
  }

  // Handle episode still in progress at end of data
  if (inEpisode) {
    const lastIdx = n - 1;
    const daysSinceStart = (new Date(navTimeseries[lastIdx].date) - new Date(navTimeseries[episodeStartIdx].date)) / 86400000;

    if (recoveryDuration === 0 || (recoveryDuration > 0 && daysSinceStart >= recoveryDuration)) {
      finalizeEpisode(lastIdx, false);
    } else {
      const drawdownPct = (troughNav - episodePeak) / episodePeak;
      const endDrawdownPct = (navTimeseries[lastIdx].nav - episodePeak) / episodePeak;
      episodes.push({
        startDate: navTimeseries[episodeStartIdx].date,
        troughDate: navTimeseries[troughIdx].date,
        endDate: navTimeseries[lastIdx].date,
        drawdownPct,
        endDrawdownPct,
        durationDays: Math.round(daysSinceStart),
        recovered: false,
        startDrawdown: drawdownSeries[episodeStartIdx - 1].drawdown,
        troughDrawdown: drawdownSeries[troughIdx].drawdown,
        jtLossFinalized: false,
        jtLossPct: 0,
        jtLossCause: 'Ongoing (within recovery window)',
      });
    }
  }

  return { episodes, jtLossEvents, drawdownSeries };
}

// ── Adaptation ───────────────────────────────────────────────────────────────

/**
 * Simulate Y_T adaptation over time for the V2 fixed-slope model.
 * Same math as computeFixedSlopeData in YdmSimulator.js.
 *
 * Slopes stay fixed; Y0 and Y100 shift with YT.
 *
 * @param {{ y0: number, yT: number, y100: number, speed: number }} ydm
 * @param {number} utilization - Current utilization [0, 1]
 * @param {number} days        - Number of days to simulate
 * @returns {{ day: number, yT: number, y0: number, y100: number }[]} 201 points
 */
function computeAdaptation({ y0, yT, y100, speed }, utilization, days) {
  const slopeLt = (yT - y0) / TARGET_U;
  const slopeGte = (y100 - yT) / (1 - TARGET_U);
  const maxSpeedPerSec = speed / SECONDS_PER_YEAR;

  // Normalized delta (same as computeNormalizedDelta in YdmSimulator.js)
  const uClamped = Math.min(Math.max(utilization, 0), 1);
  const normalizedDelta = uClamped < TARGET_U
    ? (uClamped - TARGET_U) / TARGET_U
    : (uClamped - TARGET_U) / (1 - TARGET_U);

  const spd = maxSpeedPerSec * normalizedDelta;
  const totalSec = days * 24 * 3600;
  const S = 200;
  const result = [];

  for (let i = 0; i <= S; i++) {
    const t = (i / S) * totalSec;
    const day = Math.round((t / 86400) * 100) / 100;
    const cYT = Math.max(0.0001, Math.min(1.0, yT * Math.exp(spd * t)));
    const y0N = Math.max(0, cYT - slopeLt * TARGET_U);
    const y100N = Math.min(1, cYT + slopeGte * (1 - TARGET_U));
    result.push({ day, yT: cYT, y0: y0N, y100: y100N });
  }

  return result;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Clip influence zones against subsequent FT periods and merge overlapping zones.
 */
function clipAndMergeZones(zones, ftPeriods) {
  const clipped = zones
    .map(z => {
      let end = z.end;
      for (const ft of ftPeriods) {
        if (ft.start > z.start && ft.start < end) {
          end = ft.start;
        }
      }
      return { start: z.start, end };
    })
    .filter(z => z.end > z.start);

  if (clipped.length <= 1) return clipped;
  clipped.sort((a, b) => a.start.localeCompare(b.start));
  const merged = [{ ...clipped[0] }];
  for (let i = 1; i < clipped.length; i++) {
    const prev = merged[merged.length - 1];
    if (clipped[i].start <= prev.end) {
      prev.end = clipped[i].end > prev.end ? clipped[i].end : prev.end;
    } else {
      merged.push({ ...clipped[i] });
    }
  }
  return merged;
}

function emptyResult() {
  return {
    dates: [],
    st: { cumReturn: [], trailingApy: [], dailyReturn: [] },
    jt: { cumReturn: [], trailingApy: [], dailyReturn: [] },
    fixedTermPeriods: [],
    fixedTermInfluenceZones: [],
    drawdownEvents: [],
    summary: {
      stAnnualized: 0,
      jtAnnualized: 0,
      stTotal: 0,
      jtTotal: 0,
      maxDrawdown: 0,
      days: 0,
    },
  };
}

export { computeYieldAtUtilization, backtestMarket, computeAdaptation, analyzeDrawdowns };
